<template>
  <div class="trip-detail">
    <div class="trip-detail__route">
      <div class="trip-detail__stop">
        <span class="trip-detail__dot" aria-hidden="true"></span>
        <span class="trip-detail__name"><span class="app-sr-only">Von </span>{{ route.from }}</span>
      </div>
      <span class="trip-detail__line" aria-hidden="true"></span>
      <div class="trip-detail__stop">
        <span class="trip-detail__dot trip-detail__dot--end" aria-hidden="true"></span>
        <span class="trip-detail__name"><span class="app-sr-only">Nach </span>{{ route.to }}</span>
      </div>
      <p class="trip-detail__when">{{ whenText }}</p>
    </div>

    <section class="trip-detail__section">
      <h3 class="trip-detail__heading">Status</h3>
      <app-segment
        :value="value.state"
        :items="stateItems"
        label="Status der Fahrt"
        @input="$emit('field', 'state', $event)"
      />
      <p v-if="value.state === 'CANCELED' && trip.state !== 'CANCELED'" class="trip-detail__hint trip-detail__hint--warn">
        Die Fahrt wird nach dem Speichern als storniert geführt.
      </p>
    </section>

    <section class="trip-detail__section">
      <h3 class="trip-detail__heading">Plätze</h3>
      <!-- Balken wie im Dashboard und in der Tabelle, farbig nach Belegung (unter 50 % grün, ab 50 % gelb, ab 80 % rot) -->
      <div class="trip-detail__usage">
        <app-share-bar
          class="trip-detail__bar"
          :parts="[{ value: used, color: usageColor }]"
          :total="Number(value.capacity) || 0"
        />
        <span class="trip-detail__usage-text"><strong>{{ used }}</strong> von {{ value.capacity === '' ? '–' : value.capacity }} belegt</span>
      </div>
      <p class="trip-detail__revenue">Umsatz <strong>{{ revenueText }}</strong></p>
      <div class="trip-detail__fields">
        <div class="app-form-field trip-detail__field">
          <label class="app-label" :for="fieldId('capacity')">Kapazität</label>
          <app-text-field
            :id="fieldId('capacity')"
            :value="value.capacity"
            type="number"
            min="0"
            :error="errors.capacity"
            @input="$emit('field', 'capacity', $event)"
          />
        </div>
        <div class="app-form-field trip-detail__field">
          <label class="app-label" :for="fieldId('discount')">Rabattkarten</label>
          <app-text-field
            :id="fieldId('discount')"
            :value="value.number_discount_tickets"
            type="number"
            min="0"
            :error="errors.number_discount_tickets"
            @input="$emit('field', 'number_discount_tickets', $event)"
          />
        </div>
      </div>
    </section>

    <section v-if="extras.length" class="trip-detail__section">
      <h3 class="trip-detail__heading">Zusatzplätze</h3>
      <div class="trip-detail__extras">
        <div v-for="extra in extras" :key="extra.field" class="trip-detail__extra">
          <label class="trip-detail__extra-name" :for="fieldId(extra.field)">{{ extra.name }}</label>
          <span class="trip-detail__extra-used">{{ extra.used }} belegt</span>
          <app-text-field
            :id="fieldId(extra.field)"
            class="trip-detail__extra-input"
            :value="value[extra.field]"
            type="number"
            min="0"
            :aria-label="extra.name + ': Kapazität'"
            :error="errors[extra.field]"
            @input="$emit('field', extra.field, $event)"
          />
        </div>
      </div>
    </section>

    <section class="trip-detail__section">
      <div class="trip-detail__heading-row">
        <h3 class="trip-detail__heading">Fahrplan</h3>
        <app-button icon="pencil" @click="$emit('edit-times')">Uhrzeiten bearbeiten</app-button>
      </div>
      <p v-if="stopsState === 'loading'" class="trip-detail__hint" role="status">Wird geladen …</p>
      <p v-else-if="stopsState === 'error'" class="trip-detail__hint trip-detail__hint--warn" role="alert">
        Die Haltestellen konnten nicht geladen werden.
      </p>
      <ol v-else class="trip-detail__stops">
        <li v-for="stop in stops" :key="stop.order_nr" class="trip-detail__stop-row">
          <span class="trip-detail__stop-time">{{ timeText(stop.start) }}</span>
          <span class="trip-detail__stop-name">{{ stop.name }}</span>
        </li>
      </ol>
    </section>

    <section class="trip-detail__section">
      <h3 class="trip-detail__heading">Fahrgäste</h3>
      <div>
        <app-button icon="send" @click="$emit('notify')">Nachricht an Fahrgäste</app-button>
      </div>
    </section>
  </div>
</template>

<script>
// Inhalt des Detailbereichs einer Shop-Fahrt (Kundenwunsch 13.10.2026: „die Fahrten … sehr viele Spalten und sehr wenig
// Platz. vielleicht auch mit so einer detailansicht machen, auch das bearbeiten eventuell von gültig auf ungültig
// machen, kapazitäten ändern etc. … sondergepäck … nachrichtenschreiben oder fahrt ändern“): Strecke wie im
// Verkaufsdetail, Abfahrt, dann Abschnitte Status (Gültig | Storniert), Plätze (Auslastung, Kapazität, Rabattkarten),
// Zusatzplätze (je Ticketart mit eigener Kapazität: belegt und Kapazität), Fahrplan (Halte mit Uhrzeit, „Uhrzeiten
// bearbeiten“) und Fahrgäste („Nachricht an Fahrgäste“). Bearbeitet wird ein Entwurf der Seite: value (Entwurf),
// errors; Ereignisse field (key, value), edit-times, notify. Die Halte lädt der Bereich selbst (GET shoptimetable, nur
// lesen); loadStops() lädt neu (nach „Uhrzeiten bearbeiten“).
import axios from "axios";
import moment from "moment";
import { formatEuro } from "@/utils/format";

const STATE_ITEMS = [
  { value: "ACTIVE", text: "Gültig" },
  { value: "CANCELED", text: "Storniert" }
];

export default {
  name: "ShopTripDetail",
  props: {
    trip: { type: Object, required: true },
    value: { type: Object, required: true },
    errors: { type: Object, default: () => ({}) },
    // Ticketarten mit eigener Kapazität [{ field, usedField, name }]
    extraTypes: { type: Array, default: () => [] }
  },
  data() {
    return {
      stateItems: STATE_ITEMS,
      stops: [],
      stopsState: "loading"
    };
  },
  computed: {
    // „Harlesiel, Anleger -> Bremen, Fernbusterminal“
    route() {
      const parts = String(this.trip.description || "").split(/\s*(?:->|→)\s*/);
      return { from: parts[0] || "–", to: parts.length > 1 ? parts[parts.length - 1] : "–" };
    },
    whenText() {
      const day = this.trip.day ? moment(this.trip.day).locale("de") : null;
      const date = day && day.isValid() ? day.format("dddd, DD.MM.YYYY") : this.trip.day_formatted || "";
      const meta = ["Linie " + (this.trip.line_id || "–"), this.trip.is_outward_direction ? "Hinfahrt" : "Rückfahrt"];
      return [date, this.trip.start ? this.trip.start + " Uhr" : null].filter(Boolean).join(" · ") + "  ·  " + meta.join(" · ");
    },
    used() {
      return Number(this.trip.capacity_used) || 0;
    },
    usedPercent() {
      const cap = Number(this.value.capacity);
      return cap > 0 ? Math.max(0, Math.min(100, (this.used * 100) / cap)) : 0;
    },
    usageColor() {
      return this.usedPercent >= 80 ? "st-late" : this.usedPercent >= 50 ? "st-warn" : "st-ok";
    },
    revenueText() {
      const v = this.trip.price;
      return v === null || v === undefined || v === "" ? "–" : formatEuro(Number(v));
    },
    extras() {
      return this.extraTypes.map(type => ({
        field: type.field,
        name: type.name,
        used: Number(this.trip[type.usedField]) || 0
      }));
    }
  },
  watch: {
    "trip.trip_id": {
      immediate: true,
      handler() {
        this.loadStops();
      }
    }
  },
  methods: {
    fieldId(key) {
      return "trip-" + this._uid + "-" + key;
    },
    timeText(value) {
      return value ? String(value).substr(0, 5) : "–";
    },
    async loadStops() {
      this.stopsState = "loading";
      try {
        const response = await axios.get(
          `${this.$store.state.baseUrl}shoptimetable?trip_id=${this.trip.trip_id}&version_id=${this.trip.version_id}`
        );
        const rows = Array.isArray(response.data) ? response.data : [];
        this.stops = rows.slice().sort((a, b) => (Number(a.order_nr) || 0) - (Number(b.order_nr) || 0));
        this.stopsState = "ready";
      } catch (e) {
        this.stopsState = "error";
      }
    }
  }
};
</script>

<style scoped>
.trip-detail {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}
/* Strecke wie im Verkaufsdetail: Start hohl, Ziel gefüllt, gepunktete Führungslinie */
.trip-detail__route {
  display: flex;
  flex-direction: column;
}
.trip-detail__stop {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.trip-detail__dot {
  width: 10px;
  height: 10px;
  flex: none;
  box-sizing: border-box;
  border: 2px solid var(--d-foc);
  border-radius: 50%;
}
.trip-detail__dot--end {
  background: var(--d-foc);
}
.trip-detail__line {
  width: 0;
  height: 16px;
  margin-left: 4px;
  border-left: 2px dotted var(--d-prv);
}
.trip-detail__name {
  min-width: 0;
  font-size: 15px;
  line-height: 20px;
  font-weight: 600;
  overflow-wrap: anywhere;
}
p.trip-detail__when {
  margin: 8px 0 0 22px;
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
  white-space: pre-wrap;
}
/* Abschnitte wie im Verkaufsdetail */
.trip-detail__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid var(--line-2);
}
.trip-detail__heading-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
h3.trip-detail__heading {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  letter-spacing: .02em;
  text-transform: uppercase;
  color: var(--ink-3);
}
p.trip-detail__hint {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
}
p.trip-detail__hint--warn {
  color: var(--amber);
}
/* Auslastung als Balken wie in der Tabelle */
.trip-detail__usage {
  display: flex;
  align-items: center;
  gap: 12px;
}
.trip-detail__bar {
  flex: 1 1 auto;
}
p.trip-detail__revenue {
  margin: 0;
  font-size: 13px;
  line-height: 20px;
  color: var(--ink-2);
}
p.trip-detail__revenue strong {
  font-weight: 600;
  color: var(--ink);
  font-variant-numeric: tabular-nums;
}
.trip-detail__usage-text {
  flex: none;
  font-size: 13px;
  line-height: 20px;
  color: var(--ink-2);
  font-variant-numeric: tabular-nums;
}
.trip-detail__usage-text strong {
  font-weight: 600;
  color: var(--ink);
}
.trip-detail__fields {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}
.trip-detail__field {
  flex: 1 1 140px;
  min-width: 0;
}
/* Zusatzplätze: Name, belegt, Kapazität je Zeile */
.trip-detail__extras {
  display: flex;
  flex-direction: column;
}
.trip-detail__extra {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto 96px;
  align-items: center;
  gap: 12px;
  padding: 4px 0;
  border-bottom: 1px solid var(--line-2);
}
.trip-detail__extra:last-child {
  border-bottom: 0;
}
.trip-detail__extra-name {
  min-width: 0;
  font-size: 13px;
  line-height: 20px;
  color: var(--ink);
}
.trip-detail__extra-used {
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
  font-variant-numeric: tabular-nums;
}
/* Halte kompakt: Uhrzeit und Ort */
.trip-detail__stops {
  margin: 0;
  padding: 0;
  list-style: none;
}
.trip-detail__stop-row {
  display: flex;
  gap: 12px;
  padding: 4px 0;
  font-size: 13px;
  line-height: 20px;
}
.trip-detail__stop-time {
  flex: none;
  width: 44px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
}
.trip-detail__stop-name {
  min-width: 0;
  color: var(--ink-2);
  overflow-wrap: anywhere;
}
</style>
